// Top navigation — minimal by design.
//
// Left:  logo · «Дашборд» (entry into observability — pages for stages,
//        orders, flows, traceability are reached from there as tiles)
//        · «Админ» (if user has any admin permission).
//
// Right: theme switcher (sun / moon / monitor icon) + user-menu
//        triggered by clicking the user-name button (classic pattern).
//        Settings, role pill, logout live in the dropdown.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/lib/AuthContext";
import { useTheme, type ThemeChoice } from "@/lib/theme";

// Admin-area gate.  Must stay the UNION of every permission gating a
// side-nav item in AdminLayout — otherwise a role that owns one of those
// items (e.g. DOC_OPERATOR with only document.test_report.manage) never
// sees the «Админ» entry and can't reach its own section.
const ADMIN_PERMS = [
  "user.manage",
  "role.manage",
  "audit.read",
  "station.manage",
  "station.assign",
  "inventory.manage",
  "tag.manage",
  "cycle.manage",
  "integration.manage",
  "print.use",
  "print.template.manage",
  "print.agent.manage",
  // Document issuance (W2) — DOC_OPERATOR reaches the test-report /
  // passport sections through these.
  "document.test_report.manage",
  "document.passport.manage",
  "quality.manage",
  // Директор с правом утверждения КД доходит до реестра КД.
  "quality.approve",
  "recipe.manage",
  "recipe.approve",
];

export function Nav() {
  const { user, hasPermission } = useAuth();
  const { t } = useTranslation();
  const showAdmin = !!user && ADMIN_PERMS.some((p) => hasPermission(p));

  return (
    <header className="border-b border-border bg-page/80 backdrop-blur sticky top-0 z-10">
      <div className="container-page flex items-center gap-6 h-14">
        <NavLink to="/" className="flex items-center gap-2 group">
          <Logo />
          <span className="font-mono text-xs uppercase tracking-widest text-text-muted group-hover:text-text-primary transition-colors">
            mes
          </span>
        </NavLink>
        <nav className="flex items-center gap-1 text-sm">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              [
                "px-3 py-1.5 rounded-md transition-colors",
                isActive
                  ? "text-text-primary bg-surface-hover"
                  : "text-text-secondary hover:text-text-primary hover:bg-surface-hover",
              ].join(" ")
            }
          >
            {t("nav.dashboard")}
          </NavLink>
          {showAdmin && <AdminLink />}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}

function AdminLink() {
  const { t } = useTranslation();
  return (
    <NavLink
      to="/admin"
      className={({ isActive }) =>
        [
          "px-3 py-1.5 rounded-md transition-colors text-sm flex items-center gap-1.5 border",
          isActive
            ? "text-accent-strong border-accent-strong bg-accent/10"
            : "text-text-secondary border-border hover:text-accent-strong hover:border-accent-strong",
        ].join(" ")
      }
    >
      <ShieldIcon />
      <span>{t("nav.admin")}</span>
    </NavLink>
  );
}

// ───────── User menu (classic dropdown on click of name) ─────────

function UserMenu() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  async function doLogout() {
    setOpen(false);
    await logout();
    nav("/login", { replace: true });
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
          open
            ? "text-text-primary bg-surface-hover"
            : "text-text-secondary hover:text-text-primary hover:bg-surface-hover"
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <UserIcon />
        <span className="hidden sm:inline">{user.name}</span>
        <CaretIcon />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 rounded-lg border border-border bg-page shadow-lg overflow-hidden z-20"
        >
          <div className="px-3 py-2 border-b border-border">
            <p className="text-text-primary text-sm font-medium">{user.name}</p>
            <p className="text-text-muted text-xs mt-0.5">
              {user.login ? `@${user.login} · ` : ""}
              <span className="pill pill-accent text-[10px]" title={t("nav.roleTitle")}>
                {user.role}
              </span>
            </p>
          </div>
          <MenuItem to="/settings" onPick={() => setOpen(false)} icon={<GearIcon />}>
            {t("nav.settings")}
          </MenuItem>
          <MenuItem to="/change-password" onPick={() => setOpen(false)} icon={<KeyIcon />}>
            {t("nav.changePassword")}
          </MenuItem>
          <button
            role="menuitem"
            onClick={doLogout}
            className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-text-secondary hover:bg-surface-hover hover:text-text-primary border-t border-border"
          >
            <LogoutIcon />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  to,
  onPick,
  icon,
  children,
}: {
  to: string;
  onPick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      onClick={onPick}
      className={({ isActive }) =>
        [
          "block px-3 py-2 text-sm flex items-center gap-2 transition-colors",
          isActive
            ? "bg-surface-hover text-text-primary"
            : "text-text-secondary hover:bg-surface-hover hover:text-text-primary",
        ].join(" ")
      }
    >
      {icon}
      <span>{children}</span>
    </NavLink>
  );
}

// ───────── Theme toggle (icon only) ─────────

function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  const { t } = useTranslation();
  const cycle: ThemeChoice[] = ["system", "light", "dark"];
  const title: Record<ThemeChoice, string> = {
    system: t("nav.theme.system"),
    light: t("nav.theme.light"),
    dark: t("nav.theme.dark"),
  };
  const next = () => {
    const idx = cycle.indexOf(theme);
    setTheme(cycle[(idx + 1) % cycle.length] ?? "system");
  };
  return (
    <button
      type="button"
      onClick={next}
      title={title[theme]}
      aria-label={title[theme]}
      className="p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
    >
      {theme === "system" ? <MonitorIcon /> : theme === "light" ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

// ───────── Icons ─────────

function Logo() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
      <rect x="1" y="1" width="20" height="20" rx="4" fill="var(--color-accent)" opacity="0.18" />
      <path
        d="M5 16 L5 6 L11 12 L17 6 L17 16"
        stroke="var(--color-accent-strong)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 2l-9.6 9.6" />
      <circle cx="7.5" cy="15.5" r="5.5" />
      <path d="M21 2l-3 3" />
      <path d="M15 8l3 3" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function CaretIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}
